import {
  isSectionHiddenByPrefs,
  NO_READING_PREFS,
  SECTION_TOPIC_LABELS,
  type ReadingPrefs,
} from '@/shared/lib/sectionTopics'
import { sectionUnitKey } from '@/shared/lib/sectionUnitKey'

import type { Article } from './types'

/**
 * Eine abspielbare Einheit: entweder das Audio des Artikels selbst oder das eines
 * seiner Abschnitte. Die Pfeile der Hörleiste laufen über diese Liste.
 */
export type PlayableUnit = {
  slug: string
  unitKey: string
  title: string
  audio: string
  duration: number | null
  article_position: number
}

/**
 * Baut die Playliste eines Kapitels: je Artikel erst sein eigenes Audio, dann das
 * seiner Abschnitte in Reihenfolge.
 *
 * Zwei Dinge fallen dabei heraus, und beide aus gutem Grund: Abschnitte ohne Audio
 * (Bilder zum Beispiel), und Abschnitte, die die Leseeinstellungen ausblenden - sonst
 * hörte man Text zu Karten, die gar nicht mehr auf dem Bildschirm stehen.
 */
export function buildPlayableUnits(
  articles: Article[],
  prefs: ReadingPrefs = NO_READING_PREFS
): PlayableUnit[] {
  return articles.flatMap((article) => {
    const units: PlayableUnit[] = []

    if (article.audio) {
      units.push({
        slug: article.slug,
        unitKey: article.slug,
        title: article.title,
        audio: article.audio,
        duration: article.duration ?? null,
        article_position: article.article_position ?? 0,
      })
    }

    for (const section of article.sections || []) {
      if (isSectionHiddenByPrefs(section.topic, prefs)) continue
      if (!section.audio) continue
      units.push({
        slug: article.slug,
        unitKey: sectionUnitKey(article.slug, section.id),
        title: `${article.title} – ${SECTION_TOPIC_LABELS[section.topic ?? ''] || section.topic}`,
        audio: section.audio,
        duration: section.duration ?? null,
        article_position: article.article_position ?? 0,
      })
    }

    return units
  })
}
